import { useState } from 'react';
import { useUpsertSectionAlertOverride } from '../../../api/hooks';
import type { SectionAlertResolution } from '../../../api/types';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';

interface Props {
  sectionId: number;
  resolution: SectionAlertResolution;
  onClose: () => void;
}

type IsEnabledMode = 'inherit' | 'on' | 'off';

export default function SectionAlertOverrideForm({ sectionId, resolution, onClose }: Props) {
  const upsert = useUpsertSectionAlertOverride(sectionId, resolution.rule_code);
  const [mode, setMode] = useState<IsEnabledMode>(
    resolution.source_per_field.is_enabled === 'section'
      ? (resolution.is_enabled ? 'on' : 'off')
      : 'inherit');
  const [thresholds, setThresholds] = useState<Record<string, string>>(
    Object.fromEntries(
      Object.entries(resolution.threshold_config).map(([k, v]) => [k, String(v)])
    )
  );
  const [overrideKeys, setOverrideKeys] = useState<Set<string>>(new Set(
    Object.entries(resolution.source_per_field.threshold_config)
      .filter(([_, src]) => src === 'section')
      .map(([k]) => k)
  ));

  function toggleOverride(k: string) {
    setOverrideKeys((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  }

  async function save(e?: React.FormEvent | React.MouseEvent) {
    e?.preventDefault();
    const body = {
      is_enabled: mode === 'inherit' ? null : mode === 'on',
      threshold_config_override: Object.fromEntries(
        Array.from(overrideKeys).map((k) => {
          const raw = thresholds[k];
          const num = Number(raw);
          return [k, Number.isFinite(num) ? num : raw];
        })
      ),
    };
    try {
      await upsert.mutateAsync(body);
      onClose();
    } catch {
      // basic error handling — leave the modal open
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Override · ${resolution.rule_code}`}
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} type="button">Cancel</Button>
          <Button
            variant="primary"
            onClick={(e) => save(e)}
            loading={upsert.isPending}
            type="button">
            Save
          </Button>
        </>
      )}>
      <form onSubmit={save} className="space-y-3">
        <fieldset>
          <legend className="text-xs font-semibold text-muted">Enabled</legend>
          <div className="flex gap-3">
            {(['inherit', 'on', 'off'] as IsEnabledMode[]).map((m) => (
              <label key={m} className="inline-flex items-center gap-1 text-sm">
                <input
                  type="radio" className="yada-radio" name="is_enabled" aria-label={m}
                  checked={mode === m}
                  onChange={() => setMode(m)} />
                <span className="capitalize">{m === 'inherit' ? 'Inherit' : m === 'on' ? 'On' : 'Off'}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <div>
          <span className="block text-xs font-semibold text-muted">Thresholds</span>
          <div className="space-y-1">
            {Object.entries(thresholds).map(([k, v]) => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={overrideKeys.has(k)}
                  onChange={() => toggleOverride(k)} />
                <span className="w-32">{k}</span>
                <input
                  type="text"
                  disabled={!overrideKeys.has(k)}
                  value={v}
                  onChange={(e) => setThresholds({ ...thresholds, [k]: e.target.value })}
                  className="min-h-[36px] flex-1 rounded-[12px] border border-line-strong bg-surface px-2.5 text-xs text-ink disabled:opacity-50" />
                <span className="text-xs text-muted">
                  ({resolution.source_per_field.threshold_config[k] ?? 'default'})
                </span>
              </label>
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
}
